import LockIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Box,
  Paper,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { Big } from "big.js";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { ZERO } from "../../shared/bigUtil.ts";
import { Stage } from "../../shared/CaseConfiguration.ts";
import { CostApportionment, PartyDescriptors } from "../../shared/CaseModel";
import {
  boundsTowardsAt,
  hasEqualSplits,
  shareBoundsOfBudget,
  sharesOfBudget,
  splitOfStage,
  stagesWithCosts,
  withAmountTowardsAt,
  withShareOfBudget,
} from "../../shared/costChanges.ts";
import { Budget, LockedCosts } from "../../shared/CostModel.ts";
import { percentage } from "../../shared/percentage";
import { CurrencyTextField } from "../general/CurrencyTextField.tsx";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { SectionLabel } from "../general/SectionLabel.tsx";
import { GuideMarker, Marked } from "../guide/GuideMarker.tsx";
import { QUIET_SLIDER } from "../general/tableStyles.ts";
import { CostApportionmentOfParty } from "./CostApportionments";

/**
 * An amount field that keeps what is typed until it is left or confirmed
 * with Enter, and then reports it once.
 */
const AmountField = ({
  label,
  value,
  onCommit,
  disabled,
}: {
  label: string;
  value: number;
  onCommit: (amount: number) => void;
  disabled: boolean;
}) => {
  const { currency } = useCurrency();
  const [draft, setDraft] = useState<number>();
  const commit = () => {
    if (draft !== undefined && draft !== value) onCommit(draft);
    setDraft(undefined);
  };
  return (
    <Box
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && commit()}
      sx={{ width: 140, flex: "none" }}
    >
      <CurrencyTextField
        // a stored amount the field does not hold yet shows anew
        key={value}
        currency={currency}
        ariaLabel={label}
        value={value}
        min={0}
        size="small"
        variant="outlined"
        disabled={disabled}
        onValueChange={setDraft}
      />
    </Box>
  );
};

/**
 * A share of a whole as a slider under its name and figure, in steps of 1%.
 * The figure follows the thumb while it is dragged; the share is reported
 * when it is released. The slider stops at `min` and `max`, which marks on
 * its track show. With `amount`, an amount field beside it sets the share
 * exactly: the slider then stands where it falls, between steps too.
 */
export const CostApportionmentSlider = ({
  label,
  secondaryLabel,
  value,
  min = 0,
  max = 1,
  onSliding,
  onUpdate,
  disabled = false,
  locked = false,
  color,
  caption,
  notEntered,
  amount,
  marker,
}: {
  label: string;
  // what stands beside the label: the guide's marker
  marker?: ReactNode;
  secondaryLabel: string;
  value: number;
  min?: number;
  max?: number;
  onSliding: (newPercentage: number) => void;
  onUpdate: (newPercentage: number) => void;
  disabled?: boolean;
  // locked by a settlement: shown with a lock, and not to be moved
  locked?: boolean;
  // the colour the share has in a chart beside the slider
  color?: string;
  // a remark under the slider, e.g. on what limits it
  caption?: string;
  // stands in for the figure while no share is entered: the slider then
  // rests at 0%, which nobody chose
  notEntered?: string;
  // the share as an amount, and what entering one does
  amount?: { value: number; onCommit: (amount: number) => void };
}) => {
  const [sliderValue, setSliderValue] = useState(value * 100);
  const [shown, setShown] = useState(value);
  // a stored share the slider does not stand at yet moves it there
  if (shown !== value) {
    setShown(value);
    setSliderValue(value * 100);
  }
  const within = (newValue: number) =>
    Math.max(min * 100, Math.min(newValue, max * 100));
  // every share set with this slider is saved with the case
  const { readOnly } = useCase();
  return (
    <Box sx={{ userSelect: "none" }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "baseline", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {color && <Swatch color={color} />}
          {locked && (
            <LockIcon sx={{ fontSize: "1em", color: "text.secondary" }} />
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {label}
          </Typography>
          {marker}
        </Stack>
        {notEntered !== undefined ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
          >
            {notEntered}
          </Typography>
        ) : (
          <Typography
            sx={{ fontWeight: 700, whiteSpace: "nowrap", ...TABULAR_NUMERALS }}
          >
            {percentage.format(sliderValue / 100)}{" "}
            <Typography
              component="span"
              variant="caption"
              sx={{ color: "text.secondary" }}
            >
              {secondaryLabel}
            </Typography>
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Slider
          disabled={disabled || locked || readOnly}
          sx={QUIET_SLIDER}
          color="primary"
          size="small"
          step={1}
          value={sliderValue}
          aria-label={label}
          aria-valuetext={notEntered}
          valueLabelFormat={(value) => percentage.format(value / 100)}
          valueLabelDisplay="off"
          onChangeCommitted={(ignored, value) => {
            if (typeof value !== "number") return;
            const normalized = within(value);
            setSliderValue(normalized);
            onUpdate(normalized / 100);
          }}
          onChange={(ignored, value) => {
            if (typeof value !== "number") return;
            const normalized = within(value);
            if (normalized === sliderValue) return;
            setSliderValue(normalized);
            onSliding(normalized / 100);
          }}
          marks={[
            ...(min > 0 ? [{ value: min * 100 }] : []),
            ...(max < 1 ? [{ value: max * 100 }] : []),
          ]}
        />
        {amount && (
          <AmountField
            label={label}
            value={amount.value}
            onCommit={amount.onCommit}
            disabled={disabled || locked || readOnly}
          />
        )}
      </Stack>
      {caption && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary" }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};

/** The colour a share has in the bars of the page, beside its name. */
const Swatch = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: 0.75,
      bgcolor: color,
      flex: "none",
    }}
  />
);

/**
 * The opponents are tints of the primary colour, from the first one lifted by
 * OPPONENT_LIGHTEN_START in steps of OPPONENT_LIGHTEN_STEP up to
 * OPPONENT_LIGHTEN_END, which every further one shares. The generic costs are
 * lighter than any of them, as what belongs to no opponent.
 */
const OPPONENT_LIGHTEN_START = 0.2;
const OPPONENT_LIGHTEN_STEP = 0.15;
const OPPONENT_LIGHTEN_END = 0.5;
const GENERIC_LIGHTEN = 0.75;

/** A part of a bar is named inside its segment from this share on. */
const NAMED_FROM = 0.22;
/** And states at least its share from this one on. */
const FIGURE_FROM = 0.06;

/** A segment of a bar, stating its share where there is room for it. */
const Segment = ({
  label,
  share,
  color,
  named = false,
}: {
  label: string;
  share: number;
  color: string;
  // also names the share inside the segment, where it is wide enough
  named?: boolean;
}) => {
  const { theme } = useTheme();

  return (
    <Box
      title={`${label}: ${percentage.format(share)}`}
      sx={{
        width: percentage.format(share),
        bgcolor: color,
        color: theme.palette.getContrastText(color),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        typography: "caption",
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        ...TABULAR_NUMERALS,
      }}
    >
      {named && share >= NAMED_FROM
        ? `${label} ${percentage.format(share)}`
        : share >= FIGURE_FROM && percentage.format(share)}
    </Box>
  );
};

/** What of a whole an amount is. */
const shareOf = (amount: Big, total: Big): number =>
  total.gt(0) ? amount.div(total).toNumber() : 0;

/**
 * The Cost apportionments page of a party (R13 and R14 of
 * SettlementFreezeOnSave.test.ts): one editor with two modes. On top a slider
 * per opponent for the whole budget; below it, in the mode per stage, the
 * same for every stage, with amount fields. The shares are of the party's
 * full costs, what settlements dropped included; what they locked is shown
 * and not to be moved. The top sliders set every stage, which they can only
 * while every stage has exactly the same split; they then also set the
 * party's apportionment, which splits costs entered at a stage without costs.
 */
export const CostApportionmentsOfParty = ({
  apportionment,
  budget,
  locked,
  update,
  opponents,
  parties,
}: {
  apportionment: CostApportionmentOfParty;
  budget: Budget | undefined;
  locked: LockedCosts;
  update(newValue: CostApportionment, budget?: Budget): Promise<void>;
  opponents: string[];
  parties: PartyDescriptors;
}) => {
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  // the page's own: the mode it is in
  const [mode, setMode] = useState<"budget" | "stages">("budget");
  // the share a top slider is being dragged to, until it is stored
  const [sliding, setSliding] = useState<{
    opponentId: string;
    share: number;
  }>();
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { settlements } = useCase().getCase();

  const { t } = useTranslation(undefined, {
    keyPrefix: "case.apportionments",
  });
  const { t: tCase } = useTranslation(undefined, { keyPrefix: "case" });

  // the party's costs, where it has any
  const costs =
    budget !== undefined && stagesWithCosts(budget).length > 0
      ? budget
      : undefined;
  const stages = costs ? stagesWithCosts(costs) : [];
  const total = stages.reduce(
    (sum, stage) => sum.add(splitOfStage(costs!, stage).total),
    ZERO,
  );
  const simpleOffered = costs === undefined || hasEqualSplits(costs);
  const perStage = costs !== undefined && (mode === "stages" || !simpleOffered);
  const whole = costs ? sharesOfBudget(costs) : undefined;

  const save = (newApportionment: CostApportionment, newBudget?: Budget) => {
    setSaving(true);
    update(newApportionment, newBudget)
      .then(() => {
        setSaving(false);
        setError(undefined);
        setSliding(undefined);
        console.debug(
          "Updated apportionment of party " + apportionment.partyId,
        );
      })
      .catch((error) => {
        setSaving(false);
        setSliding(undefined);
        console.warn(
          "Failed to update apportionment of party " + apportionment.partyId,
          error,
        );
        setError(error);
      });
  };
  const stored = apportionment.apportionment ?? { generic: 1, perParty: {} };
  // a top slider: the share of every stage, and of the party's apportionment
  const saveShare = (opponentId: string, share: number) => {
    const perParty = { ...stored.perParty, [opponentId]: share };
    const rest = Object.values(perParty).reduce(
      (left, of) => left.minus(of),
      new Big(1),
    );
    save(
      { generic: (rest.gt(0) ? rest : ZERO).toNumber(), perParty },
      costs && withShareOfBudget(costs, locked, opponentId, share),
    );
  };
  // a stage's slider or amount field: the costs of that stage alone
  const saveAmountAt = (stage: Stage, opponentId: string, amount: number) =>
    costs &&
    save(stored, withAmountTowardsAt(costs, locked, stage, opponentId, amount));

  const settledAt = (opponentId: string) => {
    const offerId = locked.ended[opponentId];
    const offer = settlements?.find(({ id }) => id === offerId);
    return offer && t("settled.settledAt", { stage: tCase(`stages.${Stage[offer.stage]}`) });
  };

  // what the page shows: the shares of the whole budget, or the one being
  // dragged to
  const shares = opponents.map((opponentId, index) => {
    const share = whole
      ? (whole.towards[opponentId]?.toNumber() ?? 0)
      : (stored.perParty[opponentId] ?? 0);
    return {
      opponentId,
      name: parties[opponentId]?.name || opponentId,
      stored: share,
      share: sliding?.opponentId === opponentId ? sliding.share : share,
      ended: locked.ended[opponentId] !== undefined,
      color: lighten(
        theme.palette.primary.main,
        Math.min(
          OPPONENT_LIGHTEN_START + index * OPPONENT_LIGHTEN_STEP,
          OPPONENT_LIGHTEN_END,
        ),
      ),
    };
  });
  const genericCosts = Math.max(
    0,
    1 - shares.reduce((sum, { share }) => sum + share, 0),
  );
  const genericColor = lighten(theme.palette.primary.main, GENERIC_LIGHTEN);
  // the generic costs count in the bargaining ranges of the opponents still
  // in the case, an equal part each
  const inCase = shares.filter(({ ended }) => !ended).length;
  const genericCostPerParty = genericCosts / (inCase === 0 ? 1 : inCase);

  const card = { borderRadius: 3, overflow: "hidden", p: 2.5 } as const;
  const bar = {
    display: "flex",
    borderRadius: 1.5,
    overflow: "hidden",
    bgcolor: theme.palette.action.hover,
  } as const;

  /** What each opponent bears, with the given part of the generic costs. */
  const result = (
    entry: string,
    title: string,
    description: string,
    genericPart: number,
  ) => (
    <Paper variant="outlined" sx={card}>
      <Marked entry={entry}>
        <SectionLabel component="h2">{title}</SectionLabel>
      </Marked>
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
        {description}
      </Typography>
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {shares.map(({ opponentId, name, share, color, ended }) => {
          // an opponent whose case ended keeps its share, and no more
          const generic = ended ? 0 : genericPart;
          return (
            <Box key={opponentId}>
              {/* the sum stands over the end of its bar: the line is as long as
                  the bar, and no shorter than the name and the sum need */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  columnGap: 1.5,
                  width: "fit-content",
                  minWidth: percentage.format(share + generic),
                  maxWidth: "100%",
                  color: ended ? "text.secondary" : undefined,
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    minWidth: 0,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {ended && <LockIcon sx={{ fontSize: "1em" }} />}
                  {name}
                </Typography>
                <Typography
                  sx={{ flex: "none", fontWeight: 700, ...TABULAR_NUMERALS }}
                >
                  {percentage.format(share + generic)}
                </Typography>
              </Box>
              <Box sx={{ ...bar, height: theme.spacing(3.25), mt: 0.5 }}>
                <Segment
                  label={t("chart.label.apportionedCosts")}
                  share={share}
                  color={color}
                />
                <Segment
                  label={t("chart.label.genericCosts")}
                  share={generic}
                  color={genericColor}
                />
              </Box>
            </Box>
          );
        })}
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between", mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {percentage.format(0)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {percentage.format(1)}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          mt: 1.5,
          pt: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Swatch color={genericColor} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("chart.label.genericCosts")}
        </Typography>
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2.25} sx={{ userSelect: "none" }}>
      {error && (
        <Alert severity="error">{t("error.updateFailed", { error })}</Alert>
      )}

      <Paper variant="outlined" sx={card}>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Marked entry="shares">
              <SectionLabel component="h2">{t("section.editor")}</SectionLabel>
            </Marked>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.75 }}
            >
              {t("party.description", {
                count: opponents.length,
                name: apportionment.partyName,
              })}
            </Typography>
          </Box>
          {costs && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <GuideMarker entry="modes" />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={perStage ? "stages" : "budget"}
                onChange={(ignored, value) => value && setMode(value)}
              >
                <ToggleButton value="budget" disabled={!simpleOffered}>
                  {t("mode.budget")}
                </ToggleButton>
                <ToggleButton value="stages">{t("mode.stages")}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
        </Stack>
        {!simpleOffered && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary", mt: 1 }}
          >
            {t("mode.budgetUnavailable")}
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 3, md: 5 },
            alignItems: "center",
            mt: 2,
          }}
        >
          {/* The shares of the whole budget */}
          <Stack spacing={2.5}>
            {shares.map(({ opponentId, name, stored: share, color, ended }) => {
              const bounds = costs
                ? shareBoundsOfBudget(costs, locked, opponentId)
                : { min: ZERO, max: new Big(share).add(genericCosts) };
              return (
                <CostApportionmentSlider
                  key={opponentId}
                  label={t("perParty", { name })}
                  color={color}
                  secondaryLabel={t("details.percentage")}
                  value={share}
                  min={bounds.min.toNumber()}
                  max={Math.min(1, bounds.max.toNumber())}
                  onUpdate={(newValue) => {
                    if (newValue === share) return;
                    saveShare(opponentId, newValue);
                  }}
                  onSliding={(newShare) =>
                    setSliding({ opponentId, share: newShare })
                  }
                  disabled={saving || perStage}
                  locked={ended}
                  caption={settledAt(opponentId)}
                  amount={
                    costs && !perStage
                      ? {
                          value: total.mul(share).toNumber(),
                          onCommit: (amount) =>
                            saveShare(
                              opponentId,
                              shareOf(new Big(amount), total),
                            ),
                        }
                      : undefined
                  }
                />
              );
            })}
          </Stack>

          {/* What they leave */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2.5,
              bgcolor: "background.default",
              px: 2.5,
              py: 2.25,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Swatch color={genericColor} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {t("generic")}
              </Typography>
              <GuideMarker entry="generic" />
            </Stack>
            <Typography
              variant="h3"
              component="p"
              sx={{
                fontWeight: 700,
                letterSpacing: "-.02em",
                lineHeight: 1.15,
                mt: 0.25,
                ...TABULAR_NUMERALS,
              }}
            >
              {percentage.format(genericCosts)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("genericCaption", { name: apportionment.partyName })}
            </Typography>
          </Box>
        </Box>

        {/* The party's costs as one bar */}
        <Box sx={{ ...bar, height: theme.spacing(4.25), mt: 3 }}>
          {shares.map(({ opponentId, name, share, color }) => (
            <Segment
              key={opponentId}
              named
              label={name}
              share={share}
              color={color}
            />
          ))}
          <Segment
            named
            label={t("generic")}
            share={genericCosts}
            color={genericColor}
          />
        </Box>

        {/* The shares of every stage */}
        {perStage && (
          <Stack spacing={2.5} sx={{ mt: 3 }}>
            {stages.map((stage) => {
              const split = splitOfStage(costs!, stage);
              return (
                <Box
                  key={stage}
                  sx={{
                    pt: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{ justifyContent: "space-between", mb: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {tCase(`stages.${Stage[stage]}`)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
                    >
                      {t("stage.summary", {
                        total: currency.format(split.total.toNumber()),
                        generic: percentage.format(
                          shareOf(split.generic, split.total),
                        ),
                      })}
                    </Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    {shares.map(({ opponentId, name, color, ended }) => {
                      const amount = split.towards[opponentId] ?? ZERO;
                      const bounds = boundsTowardsAt(
                        costs!,
                        locked,
                        stage,
                        opponentId,
                      );
                      return (
                        <CostApportionmentSlider
                          key={opponentId}
                          label={t("perParty", { name })}
                          color={color}
                          secondaryLabel={t("details.ofStage")}
                          value={shareOf(amount, split.total)}
                          min={shareOf(bounds.min, split.total)}
                          max={Math.min(1, shareOf(bounds.max, split.total))}
                          onUpdate={(newValue) =>
                            saveAmountAt(
                              stage,
                              opponentId,
                              split.total.mul(newValue).toNumber(),
                            )
                          }
                          onSliding={() => {}}
                          disabled={saving}
                          locked={ended}
                          amount={{
                            value: amount.toNumber(),
                            onCommit: (newAmount) =>
                              saveAmountAt(stage, opponentId, newAmount),
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2.25,
        }}
      >
        {result(
          "bargaining",
          t("section.perPartyApportionments"),
          t("results", { name: apportionment.partyName }),
          genericCostPerParty,
        )}
        {result(
          "liability",
          t("section.perPartyLiability"),
          t("recoveries", { name: apportionment.partyName }),
          genericCosts,
        )}
      </Box>
    </Stack>
  );
};
